// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import {
  Shield,
  SquarePen,
  ChevronRight,
  Trash2,
  X,
  Loader2,
} from "lucide-react";
import { roleService } from "../../services/roleService";
import InlineNotification from "../InlineNotification";
import useInlineNotification from "../../hooks/useInlineNotification";
import { usePermissions } from "../../hooks/usePermissions";
import GlobalAdminHeader from "./GlobalAdminHeader";
import PageLoader from "../PageLoader";
import LoadingOverlay from "../LoadingOverlay";

const RoleManagement = () => {
  const [expandedRoles, setExpandedRoles] = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deletingRole, setDeletingRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});

  const [newRole, setNewRole] = useState({
    name: "",
    description: "",
    permissions: [],
  });

  const [loadingPermissionsRoleId, setLoadingPermissionsRoleId] =
    useState(null);
  const [loadingEditPermissions, setLoadingEditPermissions] = useState(false);

  // Use the custom hook for notifications
  const { notification, showSuccess, showError } = useInlineNotification();
  const { hasPermission } = usePermissions();

  // Add permission checks for role permissions
  const canViewPermissions = hasPermission("role_permissions.view");
  const canManagePermissions = hasPermission("role_permissions.manage");

  const getRolePermissions = (roleId) => {
    if (!roleId) return [];
    return rolePermissions[roleId] || [];
  };

  const toggleRole = async (role) => {
    const roleId = role.role_id;
    const roleName = role.name;
    if (!roleId) {
      console.error("No role ID found for role:", role);
      return;
    }

    setExpandedRoles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(roleName)) {
        newSet.delete(roleName);
      } else {
        newSet.add(roleName);
        fetchRolePermissions(roleId, false);
      }
      return newSet;
    });
  };

  // Fetch permissions for a specific role
  const fetchRolePermissions = async (roleId, forEdit = false) => {
    if (forEdit) setLoadingEditPermissions(true);
    else setLoadingPermissionsRoleId(roleId);

    try {
      const response = await roleService.getRolePermissions(roleId);
      setRolePermissions((prev) => ({
        ...prev,
        [roleId]: response.permissions || [],
      }));
    } catch (err) {
      console.error(`Error fetching permissions for role ${roleId}:`, err);
      setRolePermissions((prev) => ({
        ...prev,
        [roleId]: [],
      }));
    } finally {
      if (forEdit) setLoadingEditPermissions(false);
      else setLoadingPermissionsRoleId(null);
    }
  };

  // Fetch all roles
  const fetchRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await roleService.getRoles();
      const rolesArray = Array.isArray(response)
        ? response
        : response?.data
          ? response.data
          : response?.items
            ? response.items
            : [];

      setRoles(rolesArray);
    } catch (err) {
      setError("Fehler beim Laden der Rollen: " + err.message);
      console.error("Error fetching roles:", err);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await roleService.rolePermissionsList();
      const permissionsArray = Array.isArray(response)
        ? response
        : response?.data
          ? response.data
          : response?.items
            ? response.items
            : [];

      setPermissions(permissionsArray);
    } catch (err) {
      console.error("Error fetching permissions:", err);
      setPermissions([]);
    }
  };

  const handleAddRole = async () => {
    if (!newRole.name.trim() || !newRole.description.trim()) {
      showError("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await roleService.createRole(newRole);
      setRoles((prev) => [...prev, response]);
      setNewRole({
        name: "",
        description: "",
        permissions: [],
      });
      setShowAddModal(false);
      showSuccess("Rolle erfolgreich erstellt!");
    } catch (err) {
      const errorMessage = "Fehler beim Erstellen der Rolle: " + err.message;
      setError(errorMessage);
      showError(errorMessage);
      console.error("Error creating role:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRole = async (role) => {
    const roleId = role.role_id;
    if (!roleId) {
      console.error("No role ID found for role:", role);
      const errorMessage = "Fehler: Keine Rollen-ID gefunden";
      setError(errorMessage);
      showError(errorMessage);
      return;
    }

    setLoadingEditPermissions(true);

    try {
      // Fetch both role permissions and available permissions in parallel
      const [rolePermsResponse, availablePermsResponse] = await Promise.all([
        roleService.getRolePermissions(roleId),
        roleService.rolePermissionsList(),
      ]);
      const rolePermNames = rolePermsResponse.permissions || [];
      const availablePerms = Array.isArray(availablePermsResponse)
        ? availablePermsResponse
        : availablePermsResponse?.data
          ? availablePermsResponse.data
          : availablePermsResponse?.items
            ? availablePermsResponse.items
            : [];
      // Map permission codes to full objects
      let selectedPermObjects = [];
      if (Array.isArray(rolePermNames) && availablePerms.length > 0) {
        selectedPermObjects = availablePerms.filter((p) => {
          return rolePermNames.includes(p.code);
        });
      }
      // Update the state for future use
      setRolePermissions((prev) => ({
        ...prev,
        [roleId]: rolePermNames,
      }));
      setPermissions(availablePerms);

      setEditingRole({
        ...role,
        originalId: roleId,
        permissions: selectedPermObjects,
      });
      setShowEditModal(true);
    } catch (error) {
      console.error("Error loading permissions:", error);
      const errorMessage = "Fehler beim Laden der Berechtigungen";
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoadingEditPermissions(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!canManagePermissions) {
      showError("Keine Berechtigung zum Verwalten von Rollenberechtigungen");
      return;
    }
    if (!editingRole.name.trim() || !editingRole.description.trim()) {
      showError("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { originalId, ...roleData } = editingRole;

      // Update role details
      const response = await roleService.updateRole(originalId, roleData);

      // Update role permissions separately
      if (editingRole.permissions && editingRole.permissions.length > 0) {
        const permissionCodes = editingRole.permissions.map((p) => p.code);
        await roleService.updateRolePermissions(originalId, permissionCodes);
      }

      // Refresh role permissions
      await fetchRolePermissions(originalId);

      setRoles((prev) =>
        prev.map((role) => {
          const currentRoleId = role.id || role.uuid || role.role_id;
          return currentRoleId === originalId ? response : role;
        }),
      );
      setEditingRole(null);
      setShowEditModal(false);
      showSuccess("Rolle erfolgreich aktualisiert!");
    } catch (err) {
      const errorMessage =
        "Fehler beim Aktualisieren der Rolle: " + err.message;
      setError(errorMessage);
      showError(errorMessage);
      console.error("Error updating role:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = (role) => {
    const roleId = role.role_id;
    if (!roleId) {
      console.error("No role ID found for role:", role);
      const errorMessage = "Fehler: Keine Rollen-ID gefunden";
      setError(errorMessage);
      showError(errorMessage);
      return;
    }
    setDeletingRole({ ...role, id: roleId });
    setShowDeleteModal(true);
  };

  const confirmDeleteRole = async () => {
    setLoading(true);
    setError(null);
    try {
      const roleId =
        deletingRole.id || deletingRole.uuid || deletingRole.role_id;
      await roleService.deleteRole(roleId);
      setRoles((prev) =>
        prev.filter((role) => {
          const currentRoleId = role.id || role.uuid || role.role_id;
          return currentRoleId !== roleId;
        }),
      );
      setDeletingRole(null);
      setShowDeleteModal(false);
      showSuccess("Rolle erfolgreich gelöscht!");
    } catch (err) {
      const errorMessage = "Fehler beim Löschen der Rolle: " + err.message;
      setError(errorMessage);
      showError(errorMessage);
      console.error("Error deleting role:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permission) => {
    setNewRole((prev) => {
      const currentPermissions = prev.permissions || [];
      const isSelected = currentPermissions.some(
        (p) => p.permission_id === permission.permission_id,
      );

      const newPermissions = isSelected
        ? currentPermissions.filter(
            (p) => !(p.permission_id === permission.permission_id),
          )
        : [...currentPermissions, permission];

      return {
        ...prev,
        permissions: newPermissions,
      };
    });
  };

  const handleEditPermissionToggle = (permission) => {
    setEditingRole((prev) => {
      const currentPermissions = prev.permissions || [];

      // Check if this permission is already selected
      const isSelected = currentPermissions.some(
        (p) => p.code === permission.code,
      );

      const newPermissions = isSelected
        ? currentPermissions.filter((p) => p.code !== permission.code)
        : [...currentPermissions, permission];

      return {
        ...prev,
        permissions: newPermissions,
      };
    });
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  return (
    <>
      <div className="relative">
        <div className="flex flex-col w-full">
          <GlobalAdminHeader
            title="Rollen"
            buttons={
              hasPermission("roles.create")
                ? [
                    {
                      text: "Rolle hinzufügen",
                      icon: <Shield className="w-4 h-4" />,
                      onClick: () => setShowAddModal(true),
                      className:
                        "flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-color text-white font-medium shadow-lg hover:opacity-90 transition-opacity",
                    },
                  ]
                : []
            }
          />

          <div className="mx-auto px-4 lg:px-4 sm:mt-6 mt-3 pb-6 w-full">
            <InlineNotification notification={notification} />

            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {loading && roles.length === 0 ? (
              <PageLoader message="Lade Rollen..." />
            ) : (
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 overflow-hidden">
                {roles.length === 0 && !loading ? (
                  <div className="text-center py-8">
                    <Shield className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-slate-400">
                      Keine Rollen gefunden
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-slate-600">
                    {(Array.isArray(roles) ? roles : []).map((role) => (
                      <div key={role.id || role.name} className="p-4">
                        <div className="flex justify-between items-center">
                          <div
                            className="flex items-center cursor-pointer"
                            onClick={() => toggleRole(role)}
                          >
                            <button
                              className="mr-2 transition-transform duration-200 text-gray-600 dark:text-slate-400"
                              style={{
                                transform: expandedRoles.has(role.name)
                                  ? "rotate(90deg)"
                                  : "rotate(0deg)",
                              }}
                            >
                              <ChevronRight className="w-5 h-5" />
                            </button>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">
                                {role.name}
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-slate-400">
                                {role.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {role.type && (
                              <span className="px-2 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 rounded-full">
                                {role.type}
                              </span>
                            )}
                            {hasPermission("roles.update") && (
                              <button
                                className="p-2 text-primary-color hover:bg-primary-color-tint rounded"
                                onClick={() => handleEditRole(role)}
                                disabled={loading}
                              >
                                <SquarePen className="w-4 h-4" />
                              </button>
                            )}
                            {hasPermission("roles.delete") && !role.type && (
                              <button
                                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                                onClick={() => handleDeleteRole(role)}
                                disabled={loading}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {expandedRoles.has(role.name) && canViewPermissions && (
                          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-600">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                              Berechtigungen (
                              {getRolePermissions(role.role_id).length})
                            </h4>
                            <div className="flex flex-wrap min-h-[40px]">
                              {loadingPermissionsRoleId === role.role_id ? (
                                <div className="col-span-2">
                                  <LoadingOverlay message="Lade Berechtigungen..." />
                                </div>
                              ) : getRolePermissions(role.role_id).length >
                                0 ? (
                                getRolePermissions(role.role_id).map(
                                  (permission, index) => (
                                    <div
                                      key={index}
                                      className="flex pb-2 w-1/2 min-w-[200px] items-center"
                                    >
                                      <div className="mr-2 h-4 w-4 text-blue-600 dark:text-blue-400 rounded border-gray-300 dark:border-slate-500 bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                                        <div className="w-2 h-2 bg-primary-color dark:bg-primary-color rounded-full"></div>
                                      </div>
                                      <span className="text-sm text-gray-700 dark:text-slate-300">
                                        {permission}
                                      </span>
                                    </div>
                                  ),
                                )
                              ) : (
                                <p className="text-sm text-gray-500 dark:text-slate-400 col-span-2">
                                  Keine Berechtigungen zugewiesen
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Role Modal - only show if user has create permission */}
      {showAddModal && hasPermission("roles.create") && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg dark:border dark:border-slate-700 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-slate-100">
                Neue Rolle erstellen
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-slate-400"
                disabled={loading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Rollenname *
                </label>
                <input
                  type="text"
                  value={newRole.name}
                  onChange={(e) =>
                    setNewRole((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:placeholder-slate-400"
                  placeholder="z.B. Verkaufsleiter"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Beschreibung *
                </label>
                <textarea
                  value={newRole.description}
                  onChange={(e) =>
                    setNewRole((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:placeholder-slate-400"
                  rows="3"
                  placeholder="Beschreibung der Rolle und ihrer Verantwortlichkeiten"
                  disabled={loading}
                />
              </div>

              {/* Only show permissions section if user has manage permissions */}
              {canViewPermissions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    Berechtigungen
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border border-gray-200 dark:border-slate-600 rounded-lg p-3 dark:bg-slate-700/30">
                    {(Array.isArray(permissions) ? permissions : []).map(
                      (permission) => (
                        <label
                          key={`add-permission-${permission.id || permission.name || 0}`}
                          className="flex items-center cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={newRole.permissions.some(
                              (p) =>
                                p.permission_id === permission.permission_id,
                            )}
                            onChange={() => handlePermissionToggle(permission)}
                            className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 dark:border-slate-500 focus:ring-blue-500 dark:bg-slate-700"
                            disabled={loading}
                          />
                          <span className="text-sm text-gray-700 dark:text-slate-300">
                            {permission.name}
                          </span>
                        </label>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/30 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
                disabled={loading}
              >
                Abbrechen
              </button>
              <button
                onClick={handleAddRole}
                disabled={
                  loading || !newRole.name.trim() || !newRole.description.trim()
                }
                className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-500 disabled:bg-gray-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed flex items-center"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Rolle erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal - only show if user has update permission */}
      {showEditModal && editingRole && hasPermission("roles.update") && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center p-2 justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg dark:border dark:border-slate-700 sm:p-6 p-4 w-full max-w-2xl sm:max-h-[90vh] h-full overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-slate-100">
                Rolle bearbeiten
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingRole(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-slate-400"
                disabled={loading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Rollenname *
                </label>
                <input
                  type="text"
                  value={editingRole.name}
                  onChange={(e) =>
                    setEditingRole((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:placeholder-slate-400"
                  placeholder="z.B. Verkaufsleiter"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Beschreibung *
                </label>
                <textarea
                  value={editingRole.description}
                  onChange={(e) =>
                    setEditingRole((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:placeholder-slate-400"
                  rows="3"
                  placeholder="Beschreibung der Rolle und ihrer Verantwortlichkeiten"
                  disabled={loading}
                />
              </div>

              {/* Only show permissions section if user has manage permissions */}
              {canViewPermissions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    Berechtigungen
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border border-gray-200 dark:border-slate-600 rounded-lg p-3 min-h-[40px] dark:bg-slate-700/30">
                    {loadingEditPermissions ? (
                      <div className="col-span-2">
                        <LoadingOverlay message="Lade Berechtigungen..." />
                      </div>
                    ) : (
                      (Array.isArray(permissions) ? permissions : []).map(
                        (permission) => (
                          <label
                            key={`edit-permission-${permission.permission_id || permission.name || 0}`}
                            className={`flex items-center cursor-pointer ${permission.permission_id}`}
                          >
                            <input
                              type="checkbox"
                              checked={(editingRole.permissions || []).some(
                                (p) => p.code === permission.code,
                              )}
                              onChange={() =>
                                handleEditPermissionToggle(permission)
                              }
                              className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 dark:border-slate-500 focus:ring-blue-500 dark:bg-slate-700"
                              disabled={loading}
                            />
                            <span className="text-sm text-gray-700 dark:text-slate-300">
                              {permission.name}
                            </span>
                          </label>
                        ),
                      )
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingRole(null);
                }}
                className="px-4 py-2 text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/30 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
                disabled={loading}
              >
                Abbrechen
              </button>
              <button
                onClick={handleUpdateRole}
                disabled={
                  loading ||
                  !editingRole.name.trim() ||
                  !editingRole.description.trim()
                }
                className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-500 disabled:bg-gray-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Änderungen speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - only show if user has delete permission */}
      {showDeleteModal && deletingRole && hasPermission("roles.delete") && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg dark:border dark:border-slate-700 p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-slate-100">
                Rolle löschen
              </h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingRole(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-slate-400"
                disabled={loading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 dark:text-slate-300 mb-2">
                Sind Sie sicher, dass Sie die Rolle{" "}
                <strong className="dark:text-slate-100">
                  {deletingRole.name}
                </strong>{" "}
                löschen möchten?
              </p>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingRole(null);
                }}
                className="px-4 py-2 text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/30 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
                disabled={loading}
              >
                Abbrechen
              </button>
              <button
                onClick={confirmDeleteRole}
                disabled={loading}
                className="px-4 py-2 bg-red-500 dark:bg-red-600 text-white rounded-lg hover:bg-red-600 dark:hover:bg-red-500 disabled:bg-red-300 dark:disabled:bg-red-900/50 flex items-center"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RoleManagement;
